import React, { useEffect, useMemo, useRef } from "react";
import Editor from "@monaco-editor/react";
import { AlertTriangle, ChevronDown, ChevronUp, GripVertical, Plus, Trash2 } from "lucide-react";
import {
  MAX_RESTRICTED_SQL_BYTES,
  MAX_RESTRICTED_SQL_MEASURES,
  RESTRICTED_SQL_MEASURE_FORMATS,
  createRestrictedSqlMeasure,
  getUtf8ByteLength,
  normalizeRestrictedSqlMeasures,
  reorderRestrictedSqlMeasures,
} from "./customReportMeasures";
import ModernTimeDropdown from "../../layouts/modernDropdown.jsx";

const EXAMPLES = [
  "COUNT(DISTINCT metadata.customer_id)",
  "COUNT(DISTINCT metadata.customer_id) FILTER (WHERE custom_module.pitched = 'Yes')",
  "COUNT(DISTINCT metadata.customer_id) FILTER (WHERE business_insights.customer_intent = 'High' AND custom_module.pitched = 'Yes')",
];

const FORMAT_LABELS = Object.fromEntries(
  RESTRICTED_SQL_MEASURE_FORMATS.map((format) => [format.value, format.label])
);

const getEditableFormat = (format) =>
  format === "percentage" ? "percentage" : format === "decimal" || format === "currency" ? "decimal" : "integer";

const getMeasureErrors = (errors, measure, index) =>
  (Array.isArray(errors) ? errors : []).filter(
    (error) =>
      (error?.measureId && String(error.measureId) === String(measure.id)) ||
      (Number.isInteger(error?.measureIndex) && error.measureIndex === index)
  );

const RestrictedSqlMeasuresEditor = ({
  reportLevel = "customers",
  visualizationId,
  measures,
  displayMeasureId,
  visualizationType,
  fieldOptions,
  errors,
  onChange,
}) => {
  const normalizedMeasures = useMemo(() => normalizeRestrictedSqlMeasures(measures), [measures]);
  const scoreField = reportLevel === "calls"
    ? fieldOptions.find((field) => field.value?.startsWith("agent_feedback."))?.value : null;
  const examples = scoreField ? [...EXAMPLES, `AVG(${scoreField})`,
    `COUNT(DISTINCT metadata.conversation_id) FILTER (WHERE ${scoreField} < 60)`] : EXAMPLES;
  const fieldsRef = useRef(fieldOptions);
  const completionDisposablesRef = useRef([]);

  useEffect(() => {
    fieldsRef.current = fieldOptions;
  }, [fieldOptions]);

  useEffect(
    () => () => {
      completionDisposablesRef.current.forEach((disposable) => disposable?.dispose?.());
      completionDisposablesRef.current = [];
    },
    []
  );

  const emitMeasures = (nextMeasures, requestedDisplayMeasureId = displayMeasureId) => {
    const validDisplayId = nextMeasures.some(
      (measure) => String(measure.id) === String(requestedDisplayMeasureId)
    )
      ? requestedDisplayMeasureId
      : nextMeasures[0]?.id;
    onChange(nextMeasures, validDisplayId);
  };

  const updateMeasure = (measureId, patch) => {
    emitMeasures(
      normalizedMeasures.map((measure) =>
        String(measure.id) === String(measureId) ? { ...measure, ...patch } : measure
      )
    );
  };

  const addMeasure = () => {
    if (normalizedMeasures.length >= MAX_RESTRICTED_SQL_MEASURES) return;
    emitMeasures([...normalizedMeasures, createRestrictedSqlMeasure(normalizedMeasures.length + 1, reportLevel)]);
  };

  const removeMeasure = (measureId) => {
    if (normalizedMeasures.length <= 1) return;
    emitMeasures(normalizedMeasures.filter((measure) => String(measure.id) !== String(measureId)));
  };

  const moveMeasure = (fromIndex, toIndex) => {
    emitMeasures(reorderRestrictedSqlMeasures(normalizedMeasures, fromIndex, toIndex));
  };

  const configureMonaco = (monaco, measureId) => {
    if (!monaco.languages.getLanguages().some((language) => language.id === "restricted-sql")) {
      monaco.languages.register({ id: "restricted-sql" });
      monaco.languages.setMonarchTokensProvider("restricted-sql", {
        ignoreCase: true,
        tokenizer: {
          root: [
            [/\b(COUNT|SUM|AVG|MIN|MAX|DISTINCT|FILTER|WHERE|CASE|WHEN|THEN|ELSE|END|COALESCE|NULLIF|ROUND|ABS|NOW|DATETIME_DIFF|AND|OR|NOT|IS|NULL|BETWEEN|IN)\b/, "keyword"],
            [/'(?:''|[^'])*'/, "string"],
            [/\b\d+(?:\.\d+)?\b/, "number"],
            [/[a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)?/, "identifier"],
            [/[=<>!+\-*/]+/, "operator"],
          ],
        },
      });
    }

    const modelPath = `/custom-report/${visualizationId}/${measureId}.restricted.sql`;
    const disposable = monaco.languages.registerCompletionItemProvider("restricted-sql", {
      triggerCharacters: ["."],
      provideCompletionItems: (model, position) => {
        if (model.uri.path !== modelPath) return { suggestions: [] };
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };
        const fieldSuggestions = fieldsRef.current.map((field) => ({
          label: field.value,
          kind: monaco.languages.CompletionItemKind.Field,
          insertText: field.value,
          detail: field.label,
          range,
        }));
        const keywordSuggestions = [
          "COUNT",
          "SUM",
          "AVG",
          "MIN",
          "MAX",
          "DISTINCT",
          "FILTER",
          "CASE",
          "COALESCE",
          "NULLIF",
          "ROUND",
          "ABS",
          "NOW",
          "DATETIME_DIFF",
        ].map((keyword) => ({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: keyword,
          range,
        }));
        return { suggestions: [...fieldSuggestions, ...keywordSuggestions] };
      },
    });
    completionDisposablesRef.current.push(disposable);
  };

  const chartNeedsDisplayMeasure = ["pie_chart", "donut_chart"].includes(visualizationType);
  const topLevelErrors = (Array.isArray(errors) ? errors : []).filter(
    (error) => !error?.measureId && !Number.isInteger(error?.measureIndex)
  );

  return (
    <div className="space-y-3" data-testid="restricted-sql-measures-editor">
      <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-3 text-xs text-indigo-950">
        <div className="font-medium">SQL measure safety boundary</div>
        <p className="mt-1 text-indigo-800">
          Enter one aggregate SQL expression, not a SELECT query.
        </p>
        <details className="mt-2">
          <summary className="cursor-pointer font-medium text-indigo-700">Allowed SQL and examples</summary>
          <div className="mt-2 space-y-2 text-gray-700">
            <p>
              Functions: COUNT, SUM, AVG, MIN, MAX, COALESCE, NULLIF, ROUND, ABS, NOW, DATETIME_DIFF. Operators: =, !=,
              &lt;&gt;, &gt;, &gt;=, &lt;, &lt;=, AND, OR, NOT, IS NULL, IS NOT NULL, BETWEEN, IN, +, -, *, /.
              DISTINCT, FILTER (WHERE ...), and CASE WHEN ... THEN ... ELSE ... END are supported.
            </p>
            <p>
              Use NOW() for the report execution time and DATETIME_DIFF('seconds', start, end)
              for elapsed time (end minus start). Units: seconds, minutes, hours, days; days are
              24 hours and results are not rounded. Use Date or Date and Time fields from the
              field list. Invalid or missing dates become null. Dates use midnight and datetimes
              without an offset use Asia/Kolkata; explicit offsets are preserved.
              Reports using NOW() recalculate on every run. Refresh an open report to update it.
            </p>
            <p>
              Example condition: <code>DATETIME_DIFF('seconds', datetime_field, NOW()) &gt; 7200</code>.
              Replace datetime_field with an available qualified field, put the condition inside
              FILTER (WHERE ...), and apply your pending/completed/cancelled rules separately.
            </p>
            <p>
              Not allowed: SELECT/FROM/JOIN/WITH/UNION, subqueries, comments, semicolons, window
              functions, casts, JSON/regex operators, schema/table names, or unapproved functions.
            </p>
            <div className="space-y-1 font-mono text-[11px]">
              {examples.map((example) => (
                <div key={reportLevel === "calls" ? example.replaceAll("metadata.customer_id", "metadata.conversation_id") : example} className="rounded border border-indigo-100 bg-white px-2 py-1 break-all">
                  {reportLevel === "calls" ? example.replaceAll("metadata.customer_id", "metadata.conversation_id") : example}
                </div>
              ))}
            </div>
          </div>
        </details>
      </div>

      {topLevelErrors.map((error, index) => (
        <div
          key={`measure-top-error-${index}`}
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error.message}</span>
        </div>
      ))}

      <div className="space-y-3">
        {normalizedMeasures.map((measure, measureIndex) => {
          const measureErrors = getMeasureErrors(errors, measure, measureIndex);
          return (
            <div key={measure.id} className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <GripVertical className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate text-sm font-medium text-gray-800">
                    {measure.label || `Measure ${measureIndex + 1}`}
                  </span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
                    {measure.id}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${measure.label || `measure ${measureIndex + 1}`} up`}
                    disabled={measureIndex === 0}
                    onClick={() => moveMeasure(measureIndex, measureIndex - 1)}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${measure.label || `measure ${measureIndex + 1}`} down`}
                    disabled={measureIndex === normalizedMeasures.length - 1}
                    onClick={() => moveMeasure(measureIndex, measureIndex + 1)}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${measure.label || `measure ${measureIndex + 1}`}`}
                    disabled={normalizedMeasures.length <= 1}
                    onClick={() => removeMeasure(measure.id)}
                    className="rounded p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs text-gray-500" htmlFor={`${measure.id}-label`}>
                    Label
                  </label>
                  <input
                    id={`${measure.id}-label`}
                    value={measure.label}
                    maxLength={100}
                    onChange={(event) => updateMeasure(measure.id, { label: event.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500" htmlFor={`${measure.id}-format`}>
                    Format
                  </label>
                  <ModernTimeDropdown
                    id={`${measure.id}-format`}
                    ariaLabel="Format"
                    value={FORMAT_LABELS[getEditableFormat(measure.format)]}
                    options={RESTRICTED_SQL_MEASURE_FORMATS.map((format) => format.label)}
                    onChange={(event) =>
                      updateMeasure(measure.id, { format: event.target.value.toLowerCase() })
                    }
                    width="w-full"
                    height="h-[38px]"
                    backgroundColor="bg-white"
                  />
                  {getEditableFormat(measure.format) === "percentage" ? (
                    <p className="mt-1 text-[11px] leading-4 text-gray-500">
                      Enter a ratio: <span className="font-mono">0.25</span> is displayed as 25%.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-3">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <label className="block text-xs text-gray-500">Aggregate SQL expression</label>
                  <select
                    aria-label={`Insert approved field into ${measure.label || `measure ${measureIndex + 1}`}`}
                    value=""
                    onChange={(event) => {
                      const field = event.target.value;
                      if (!field) return;
                      const prefix = measure.sqlExpression.trim() ? `${measure.sqlExpression} ` : "";
                      updateMeasure(measure.id, { sqlExpression: `${prefix}${field}` });
                    }}
                    className="max-w-full rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-600"
                  >
                    <option value="">Insert approved field...</option>
                    {fieldOptions.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.value} — {field.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="overflow-hidden rounded-md border border-gray-300">
                  <Editor
                    height="112px"
                    path={`/custom-report/${visualizationId}/${measure.id}.restricted.sql`}
                    language="restricted-sql"
                    theme="vs-light"
                    value={measure.sqlExpression}
                    beforeMount={(monaco) => configureMonaco(monaco, measure.id)}
                    onChange={(value) => updateMeasure(measure.id, { sqlExpression: value || "" })}
                    options={{
                      minimap: { enabled: false },
                      lineNumbers: "off",
                      glyphMargin: false,
                      folding: false,
                      lineDecorationsWidth: 8,
                      lineNumbersMinChars: 0,
                      overviewRulerLanes: 0,
                      scrollBeyondLastLine: false,
                      wordWrap: "on",
                      fontSize: 12,
                      tabSize: 2,
                      padding: { top: 8, bottom: 8 },
                      quickSuggestions: true,
                      suggestOnTriggerCharacters: true,
                      automaticLayout: true,
                    }}
                  />
                </div>
                <div className="mt-1 text-right text-[10px] text-gray-400">
                  {getUtf8ByteLength(measure.sqlExpression)} / {MAX_RESTRICTED_SQL_BYTES.toLocaleString("en-US")} bytes
                </div>
              </div>

              {measureErrors.map((error, index) => (
                <div key={`${measure.id}-error-${index}`} className="mt-2 text-xs text-red-600" role="alert">
                  {error.message}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addMeasure}
        disabled={normalizedMeasures.length >= MAX_RESTRICTED_SQL_MEASURES}
        className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        Add SQL measure ({normalizedMeasures.length}/{MAX_RESTRICTED_SQL_MEASURES})
      </button>

      {chartNeedsDisplayMeasure ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor={`${visualizationId}-display-measure`}>
            Measure shown in the chart
          </label>
          <select
            id={`${visualizationId}-display-measure`}
            value={displayMeasureId || ""}
            onChange={(event) => emitMeasures(normalizedMeasures, event.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="" disabled>
              Select a measure
            </option>
            {normalizedMeasures.map((measure) => (
              <option key={measure.id} value={measure.id}>
                {measure.label || measure.id}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
};

export default RestrictedSqlMeasuresEditor;
